import React, { useState, useEffect } from 'react';
import { merchantService, OrganizationRegion } from '../../services/merchantService';

interface RegionSelectorProps {
  organizationId?: string;
  value?: string;
  onChange: (regionId: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  showEmptyOption?: boolean;
}

const RegionSelector: React.FC<RegionSelectorProps> = ({
  organizationId,
  value,
  onChange,
  disabled = false,
  required = false,
  placeholder = 'Sélectionnez une région',
  className = '',
  showEmptyOption = true
}) => {
  const [regions, setRegions] = useState<OrganizationRegion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRegions = async () => {
      if (!organizationId) {
        setRegions([]);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await merchantService.getOrganizationRegions(organizationId);
        setRegions(response.regions);
      } catch (err) {
        console.error('Error loading regions:', err);
        setError('Impossible de charger les régions');
        setRegions([]);
      } finally {
        setLoading(false);
      }
    };

    loadRegions();
  }, [organizationId]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  if (!organizationId) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
        >
          <option value="">Sélectionnez d'abord une marque globale</option>
        </select>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500"
        >
          <option value="">Chargement des régions...</option>
        </select>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <select
          disabled
          className="w-full px-3 py-2 border border-red-300 rounded-md shadow-sm bg-red-50 text-red-500"
        >
          <option value="">{error}</option>
        </select>
        <p className="mt-1 text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={disabled || regions.length === 0}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        {showEmptyOption && <option value="">{placeholder}</option>}
        {regions.map((region) => (
          <option key={region.region_id} value={region.region_id}>
            {region.name} {!region.is_active && '(Inactive)'}
          </option>
        ))}
      </select>
      {regions.length === 0 && !loading && organizationId && (
        <p className="mt-1 text-sm text-gray-500">Aucune région disponible pour cette marque globale</p>
      )}
    </div>
  );
};

export default RegionSelector;